import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api";

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para agregar token JWT a las peticiones
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("sgcm_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo de respuestas y errores
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response) {
      // Error del servidor con respuesta
      const { status, data } = error.response;

      if (status === 401) {
        // Token expirado o inválido - Solo redirigir si NO estamos en login
        const currentPath = window.location.pathname;
        if (currentPath !== "/login" && currentPath !== "/") {
          localStorage.removeItem("sgcm_token");
          localStorage.removeItem("sgcm_user");
          window.location.href = "/login";
          return; // Salir para evitar el throw
        }
      }

      // Si hay errores de validación, incluirlos en el mensaje
      let errorMessage = data.message || data.error || "Error del servidor";

      if (data.validationErrors && Array.isArray(data.validationErrors)) {
        const validationMessages = data.validationErrors
          .map((err) => `${err.field || err.path}: ${err.message || err.msg}`)
          .join(", ");
        errorMessage = `${errorMessage} - ${validationMessages}`;
      }

      throw new Error(errorMessage);
    } else if (error.request) {
      // Error de red
      throw new Error("Error de conexión. Verifica tu conexión a internet.");
    } else {
      // Error de configuración
      throw new Error("Error en la petición.");
    }
  }
);

// Servicios base para operaciones CRUD
export const apiService = {
  // Métodos HTTP básicos
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),

  // Métodos para subida de archivos
  postFile: (url, formData, onUploadProgress = null) => {
    return apiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
  },

  // Método para descargar archivos
  downloadFile: async (url, filename = null) => {
    try {
      const response = await apiClient.get(url, {
        responseType: "blob",
      });

      // Crear URL del blob y descargar
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Método para obtener URL de preview
  getPreviewUrl: (catalogId) => {
    return `${API_BASE_URL}/catalogs/${catalogId}/preview`;
  },

  // Método para obtener URL de descarga
  getDownloadUrl: (catalogId) => {
    return `${API_BASE_URL}/catalogs/${catalogId}/download`;
  },

  // Método para verificar salud del servidor
  healthCheck: () => apiClient.get("/health"),
};

export default apiService;
